// ════════════════════════════════════════════════════════════════════════════
//  ManDana — organizer proposal authority (Phase 1B-1)
//  ---------------------------------------------------------------------------
//  The organizer prepares tag-scoped cell values for review. It never edits a
//  Record's title or body: its entire output lives on membership edges (the
//  D3 per-property metadata map — see md-membership.ts) and in the four small
//  document families below.
//
//    md:proposal:<id>   one immutable candidate for one target cell. Nothing
//                       rewrites it; a rerun creates a successor linked by
//                       `pred`, and a duplicate idempotency key returns the
//                       existing proposal instead of creating anything.
//    md:decision:<id>   one append-only decision event (materialize / accept /
//                       reject / unlock). Effective review/protection state is
//                       a deterministic fold over these events — sorted by
//                       (at, id), first terminal decision wins — so two
//                       devices that both decided converge to the same answer
//                       once both decision documents arrive.
//    md:orgschema:<id>  versioned schema authority: the property ids a tag
//                       declares, at a semantic version. The planner refuses
//                       SCHEMA_MISMATCH when the authority has moved past the
//                       version the proposal was derived from.
//    md:orgpolicy:<id>  versioned policy authority: the autofill confidence
//                       threshold, at a semantic version. Confidence selects
//                       the policy action (materialize vs. review-only); it
//                       never changes lifecycle states (D3).
//
//  LOCAL-FIRST, BUT NEVER OPTIMISTIC ABOUT VALUES. Proposals and decisions
//  are ordinary local documents: durable offline, synced by the normal
//  push/pull. A cell VALUE, though, changes only inside a guarded A9 group
//  commit (mdSyncCommit) whose read-set pins the proposal, the source Record
//  document for body-derived candidates, the target edge, and the schema and
//  policy authorities by their retained server revisions —
//  with ABSENT used only for the decision being created. The executor writes
//  nothing locally before the Worker accepts the group, so a stale read-set
//  rolls back to exactly the local state that was already there, and a retry
//  re-plans against current state instead of replaying a stale plan.
//
//  A document whose server revision this device has not retained (created
//  offline, or pushed before revisions were recorded) stays perfectly
//  readable — it just cannot authorize a guarded apply, and the planner says
//  so by name: OFFLINE_UNCOMMITTED.
// ════════════════════════════════════════════════════════════════════════════

/* The D3 tuple slots, exactly as md-membership.ts stores them per property:
   origin 'ai' | 'user' | 'migration:<id>', review 'proposed' | 'accepted' |
   'rejected', protection 'mutable' | 'user-locked', plus provenance. */
type MdOrganizerAct = 'materialize' | 'accept' | 'reject' | 'unlock';

type MdOrganizerRefusal =
  | 'STALE_INPUT'          // the world moved since the candidate was derived
  | 'TARGET_MISSING'       // the membership edge is gone (tag removed)
  | 'SCHEMA_MISMATCH'      // schema authority missing, or version/property drift
  | 'POLICY_MISMATCH'      // policy authority missing, or version drift
  | 'USER_LOCKED'          // a user-locked (or unowned) value always wins
  | 'ALREADY_DECIDED'      // the requested transition already happened
  | 'INVALID_PROPOSAL'     // malformed proposal or impossible transition
  | 'OFFLINE_UNCOMMITTED'  // no retained server revision; cannot guard
  | 'BELOW_THRESHOLD'      // policy does not permit materializing this confidence
  | 'STALE_READ_SET';      // the Worker's own refusal, passed through unchanged

interface MdOrganizerProvenance {
  model?: string;
  prompt?: string;         // prompt version, e.g. 'book-enrichment:4'
  refs?: string[];         // source references the candidate was derived from
  conf: number;            // confidence in [0, 1]
}

interface MdOrganizerInputVector {
  bodyRev: number | null;  // semantic Record revision the excerpt came from
  bodyHash: string | null; // canonical title/body/tag fingerprint
  sourceKey: string | null;// opaque sync document that owns that Record
  sourceRev: number | null;// retained server revision captured at proposal time
  schema: number;          // semantic schema version observed
  schemaId: string;
  targetRev: number | null;// server revision of the target edge when observed
  policy: number;          // semantic policy version observed
  policyId: string;
}

interface MdOrganizerProposal {
  id: string;              // [a-z0-9]{6,40} — server key proposal:<id>
  runId: string;           // one organizer execution; groups output, owns nothing
  ikey: string;            // idempotency key: rule + target + input + candidate
  rule: string;            // the producing rule, e.g. 'book-enrichment'
  target: { edge: string; property: string };
  candidate: unknown;      // the proposed cell value (JSON, bounded)
  origin: 'ai';
  input: MdOrganizerInputVector;
  prov: MdOrganizerProvenance;
  pred: string | null;     // predecessor proposal id when this is a rerun
  at: number;
}

interface MdOrganizerDecision {
  id: string;              // [a-z0-9]{6,40} — server key decision:<id>
  pid: string;             // the proposal this decides
  act: MdOrganizerAct;
  actor: string;           // 'user' | 'organizer' | 'policy:<policyId>@<v>'
  reason?: string;         // e.g. 'stale-input' on a stale rejection
  at: number;
}

interface MdOrganizerSchema {
  id: string;              // [a-z0-9]{6,40} — server key orgschema:<id>
  tag: string;             // the tag this is the schema of, e.g. 'book'
  v: number;               // semantic schema version, integer >= 1
  properties: Array<{ id: string; type: string; label?: string }>;
  at: number;
}

interface MdOrganizerPolicy {
  id: string;              // [a-z0-9]{6,40} — server key orgpolicy:<id>
  scope: string;           // what the policy governs, e.g. 'book'
  v: number;               // semantic policy version, integer >= 1
  autofill: number;        // confidence threshold in [0, 1]
  at: number;
}

interface MdOrganizerState {
  review: 'proposed' | 'accepted' | 'rejected';
  protection: 'mutable' | 'user-locked' | null;
  materialized: boolean;
  decisions: MdOrganizerDecision[];
}

/* The semantic world a plan was derived from: the full sanitised proposal
   plus every guarded authority/edge document the planner read. The executor's
   signature covers it whole, so a plan can never be applied against a local
   proposal whose candidate, provenance or input vector was rewritten after
   issue — including the acts (reject, accept over an already-materialized
   cell) whose own output never carries those fields. */
interface MdOrganizerPlanSnapshot {
  proposal: MdOrganizerProposal;
  edge?: MdOrganizerEdge;
  schema?: MdOrganizerSchema;
  policy?: MdOrganizerPolicy;
}

interface MdOrganizerPlanOk {
  ok: true;
  pid: string;
  act: MdOrganizerAct;
  readSet: Array<{ key: string; expectedRev: number | 'ABSENT' }>;
  docs: Array<{ key: string; value: unknown }>;
  snapshot: MdOrganizerPlanSnapshot;
  observedBodyHash: string | null;
}

/* What a tombstoned proposal leaves behind (Scenario 4 audit retention):
   IDs/hashes/status/timestamps only — the proposed content and its evidence
   are gone, so tag removal genuinely removes Organizer-derived data, while
   the audit trail stays interpretable. A stub is deliberately NOT a valid
   proposal: every reader (proposal lookup, planner, sync-docs) refuses it,
   and a tombstoned id never accepts a live document again — superseded or
   rejected proposals never reactivate; reconsideration is a successor under
   a new id. */
interface MdOrganizerProposalStub {
  id: string;
  tombstone: true;
  at: number | null;          // the proposal's original creation stamp
  tombstonedAt: number;       // when this device folded the cleanup
  hash: string | null;        // content hash of the last held copy
  review: 'proposed' | 'accepted' | 'rejected' | null; // last folded status
}

interface MdOrganizerPlanRefusal {
  ok: false;
  refusal: MdOrganizerRefusal;
  detail?: string;
  conflicts?: unknown;
}

type MdOrganizerPlan = MdOrganizerPlanOk | MdOrganizerPlanRefusal;

/* Structural twin of md-membership.ts's cell metadata — the modules share a
   storage shape, not a compilation unit. */
interface MdOrganizerCellMeta {
  o: string;
  r: string;
  p: string;
  prov?: {
    model?: string; prompt?: string; refs?: string[]; conf?: number;
    pid?: string; run?: string; at?: number;
  };
}

interface MdOrganizerEdge {
  id: string;
  dbId: string;
  k: string;
  v: Record<string, unknown>;
  at: number;
  order: number;
  m?: Record<string, MdOrganizerCellMeta>;
}

(function (g: any) {
  'use strict';

  const ID_RE = /^[a-z0-9]{6,40}$/;
  const PROPERTY_RE = /^[\w:/#.-]{1,200}$/;
  const SOURCE_KEY_RE = /^(?:events|records):\d{4}-\d{2}-\d{2}$|^record:[a-z0-9]{6,40}$|^project:[a-z0-9]{6,40}$|^project-records$|^domain:e\d{1,3}$|^pursuit:[a-z0-9]{6,40}$/;
  const MAX_PROPOSALS = 5000;
  const MAX_CANDIDATE_BYTES = 64 * 1024;
  const MAX_SCHEMA_PROPERTIES = 200;

  let seq = 0;
  function newId(prefix: string): string {
    return prefix + Date.now().toString(36) + (seq++).toString(36)
      + Math.random().toString(36).slice(2, 7);
  }

  const validId = (value: unknown): value is string =>
    typeof value === 'string' && ID_RE.test(value);
  const validPropertyId = (value: unknown): value is string =>
    typeof value === 'string' && PROPERTY_RE.test(value);
  const validSourceKey = (value: unknown): value is string =>
    typeof value === 'string' && SOURCE_KEY_RE.test(value);
  const text = (value: unknown, max: number): string | null =>
    typeof value === 'string' && value.length >= 1 && value.length <= max ? value : null;
  const intOrNull = (value: unknown): number | null =>
    Number.isInteger(value) && (value as number) >= 0 ? value as number : null;
  const versionOrNull = (value: unknown): number | null =>
    Number.isInteger(value) && (value as number) >= 1 ? value as number : null;

  const refusal = (name: MdOrganizerRefusal, detail?: string): MdOrganizerPlanRefusal =>
    detail ? { ok: false, refusal: name, detail } : { ok: false, refusal: name };

  /* ── candidates and provenance ─────────────────────────────────────────── */

  /* A candidate is anything JSON can faithfully carry, bounded so a proposal
     document can never approach the server's per-document cap on its own. */
  function sanitiseCandidate(value: unknown): unknown {
    let serialised: string;
    try { serialised = JSON.stringify(value); } catch (e) { return undefined; }
    if (typeof serialised !== 'string' || serialised.length > MAX_CANDIDATE_BYTES) {
      return undefined;
    }
    return value;
  }

  function strictDate(value: unknown): string | null {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(5, 7));
    const day = Number(value.slice(8, 10));
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day ? value : null;
  }

  function safeUrl(value: unknown): string | null {
    if (typeof value !== 'string' || value.length > 2048) return null;
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password) return null;
      url.hash = '';
      return url.toString();
    } catch (e) { return null; }
  }

  function safeRef(value: unknown): string | null {
    return typeof value === 'string' && value.length <= 300 ? safeUrl(value) : null;
  }

  /* Canonical schema boundary for provider mapping, planners and rendering.
     It returns the exact value safe to persist, never a truthy guess. */
  function mdOrganizerCandidateValidate(schemaId: unknown, propertyId: unknown,
    value: unknown): { ok: true; value: unknown } | { ok: false; reason: string } {
    const schema = mdOrganizerSchemaGet(schemaId);
    if (!schema || !validPropertyId(propertyId)) return { ok: false, reason: 'schema' };
    const property = schema.properties.find((item) => item.id === propertyId);
    if (!property) return { ok: false, reason: 'property' };
    let clean: unknown;
    if (property.type === 'date') clean = strictDate(value);
    else if (property.type === 'url') clean = safeUrl(value);
    else if (property.type === 'number') clean = typeof value === 'number' && Number.isFinite(value)
      ? value : undefined;
    else if (property.type === 'checkbox') clean = typeof value === 'boolean' ? value : undefined;
    else if (property.type === 'record-ref-list') {
      clean = Array.isArray(value) && value.length <= 20
        && value.every((item) => typeof item === 'string' && !!g.mdRecordRefParse(item))
        ? [...new Set(value)] : undefined;
    } else if (property.type === 'multi' || property.type === 'person') {
      clean = Array.isArray(value) && value.length <= 20
        && value.every((item) => typeof item === 'string' && item.length <= 500)
        ? [...new Set(value)] : undefined;
    } else {
      clean = typeof value === 'string' && value.trim() && value.trim().length <= 4000
        ? value.trim() : undefined;
    }
    if (clean == null || sanitiseCandidate(clean) === undefined) {
      return { ok: false, reason: property.type };
    }
    return { ok: true, value: clean };
  }

  function sanitiseProvenance(value: unknown, requireConfidence: boolean): MdOrganizerProvenance | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as Record<string, unknown>;
    const conf = typeof raw.conf === 'number' && Number.isFinite(raw.conf)
      && raw.conf >= 0 && raw.conf <= 1 ? raw.conf : null;
    if (conf == null && requireConfidence) return null;
    const out: MdOrganizerProvenance = { conf: conf == null ? 0 : conf };
    const model = text(raw.model, 120); if (model) out.model = model;
    const prompt = text(raw.prompt, 120); if (prompt) out.prompt = prompt;
    if (Array.isArray(raw.refs)) {
      out.refs = [...new Set(raw.refs.map(safeRef).filter(Boolean))].slice(0, 20) as string[];
    }
    return out;
  }

  /* The compact provenance an edge cell carries: the full record stays on the
     immutable proposal; the cell keeps enough to render and a pointer back. */
  function compactProvenance(proposal: MdOrganizerProposal, at: number): MdOrganizerCellMeta['prov'] {
    const prov: NonNullable<MdOrganizerCellMeta['prov']> = {
      conf: proposal.prov.conf, pid: proposal.id, run: proposal.runId, at,
    };
    if (proposal.prov.model) prov.model = proposal.prov.model;
    if (proposal.prov.prompt) prov.prompt = proposal.prov.prompt;
    if (proposal.prov.refs) prov.refs = [...proposal.prov.refs];
    return prov;
  }

  /* ── proposals ─────────────────────────────────────────────────────────── */

  function sanitiseProposal(value: unknown, expectedId?: string): MdOrganizerProposal | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as Partial<MdOrganizerProposal>;
    const id = expectedId || raw.id;
    if (!validId(id) || (raw.id != null && raw.id !== id)) return null;
    if (!text(raw.runId, 120) || !text(raw.ikey, 160) || !text(raw.rule, 60)) return null;
    if (raw.origin !== 'ai') return null;
    const target = raw.target;
    if (!target || typeof target !== 'object'
      || !validId((target as { edge?: unknown }).edge)
      || !validPropertyId((target as { property?: unknown }).property)) return null;
    const candidate = sanitiseCandidate(raw.candidate);
    if (candidate === undefined) return null;
    const input = raw.input;
    if (!input || typeof input !== 'object') return null;
    /* ABSENT IS VALID, INVALID IS NOT. Each slot may be null (an offline
       observation, legitimately) — but a PRESENT value that fails its shape
       (negative or fractional revision, non-string hash) marks the whole
       document as written by something that was not an honest organizer.
       Coercing it to null would silently launder a corrupt audit trail into
       a clean one, so the document is rejected whole instead of repaired. */
    const strictIntSlot = (v: unknown): number | null | undefined =>
      v == null ? null : intOrNull(v) ?? undefined;
    const strictTextSlot = (v: unknown, max: number): string | null | undefined =>
      v == null ? null : text(v, max) ?? undefined;
    const bodyRev = strictIntSlot(input.bodyRev);
    const targetRev = strictIntSlot(input.targetRev);
    const sourceRev = strictIntSlot(input.sourceRev);
    const bodyHash = strictTextSlot(input.bodyHash, 128);
    const sourceKey = input.sourceKey == null ? null
      : validSourceKey(input.sourceKey) ? input.sourceKey : undefined;
    if (bodyRev === undefined || targetRev === undefined || sourceRev === undefined
      || bodyHash === undefined || sourceKey === undefined) return null;
    if (!versionOrNull(input.schema) || !validId(input.schemaId)
      || !versionOrNull(input.policy) || !validId(input.policyId)) return null;
    const prov = sanitiseProvenance(raw.prov, true);
    if (!prov) return null;
    if (raw.pred != null && !validId(raw.pred)) return null;
    if (!Number.isFinite(raw.at)) return null;
    return {
      id,
      runId: raw.runId as string,
      ikey: raw.ikey as string,
      rule: raw.rule as string,
      target: { edge: target.edge, property: target.property },
      candidate,
      origin: 'ai',
      input: {
        bodyRev, bodyHash, sourceKey, sourceRev,
        schema: input.schema, schemaId: input.schemaId,
        targetRev,
        policy: input.policy, policyId: input.policyId,
      },
      prov,
      pred: raw.pred == null ? null : raw.pred as string,
      at: raw.at as number,
    };
  }

  function proposalKey(id: string): string { return 'md:proposal:' + id; }

  /* ── the proposal set, read once per device revision ─────────────────────
     Reading every proposal means walking every key on the device and parsing
     every proposal document, and the question callers actually ask is almost
     always the narrow one: "anything for this one edge?" md-book asks it for
     EVERY record at boot — twice, once to look and once to tombstone — and on
     an account of five thousand records that was two full passes over storage
     per record, for records that own no proposals and write nothing.

     So the set is built once and kept until the device changes. The signal is
     mdStorageRevision(): the storage chokepoint counts every write and every
     removal, so any change anywhere — a local propose, a tombstone, a document
     arriving over sync — retires this. Coarse on purpose. A cache that tried
     to reason about which keys it depended on would be wrong about the one it
     forgot, and a stale proposal set is a wrong answer about a user's data.

     `byTarget` is the same set indexed by the question, sorted once at build
     time, so the narrow ask is a lookup rather than a filter over everything.
     Falls back to reading fresh every time if the counter is absent, so a
     build without it behaves exactly as before. */
  interface MdProposalSet {
    revision: number | null;
    all: MdOrganizerProposal[];
    byTarget: Map<string, MdOrganizerProposal[]>;
  }
  let proposalMemo: MdProposalSet | null = null;

  function proposalSet(): MdProposalSet {
    const revision = typeof g.mdStorageRevision === 'function' ? g.mdStorageRevision() : null;
    if (proposalMemo && revision !== null && proposalMemo.revision === revision) return proposalMemo;
    const all: MdOrganizerProposal[] = [];
    const byTarget = new Map<string, MdOrganizerProposal[]>();
    for (const key of g.mdStorageKeys('md:proposal:')) {
      const id = key.slice('md:proposal:'.length);
      const proposal = sanitiseProposal(g.mdRead(key, null), id);
      if (proposal) {
        all.push(proposal);
        const held = byTarget.get(proposal.target.edge);
        if (held) held.push(proposal); else byTarget.set(proposal.target.edge, [proposal]);
      }
      if (all.length >= MAX_PROPOSALS) break;
    }
    for (const held of byTarget.values()) {
      held.sort((a, b) => b.at - a.at || b.id.localeCompare(a.id));
    }
    const built: MdProposalSet = { revision, all, byTarget };
    if (revision !== null) proposalMemo = built;
    return built;
  }

  function allProposals(): MdOrganizerProposal[] {
    return proposalSet().all.slice();
  }

  function mdOrganizerProposal(id: unknown): MdOrganizerProposal | null {
    if (!validId(id)) return null;
    return sanitiseProposal(g.mdRead(proposalKey(id), null), id);
  }

  /* djb2, twice with different seeds, over the canonical identity string —
     rule + target + input + candidate per the contract. The authority
     IDENTITIES belong to that identity: two different schemas or policies
     that merely share a version number are different worlds, and must yield
     a successor proposal, never collapse into one. Only ever compared to
     itself; 64 bits of it makes an accidental duplicate a non-event. */
  function idempotencyKey(rule: string, target: { edge: string; property: string },
    input: MdOrganizerInputVector, candidate: unknown): string {
    const canonical = [rule, target.edge, target.property,
      input.bodyRev ?? '', input.bodyHash ?? '', input.sourceKey ?? '', input.sourceRev ?? '',
      input.schema, input.schemaId, input.targetRev ?? '',
      input.policy, input.policyId, JSON.stringify(candidate)].join('\u0001');
    let h1 = 5381; let h2 = 52711;
    for (let i = 0; i < canonical.length; i++) {
      h1 = ((h1 << 5) + h1 + canonical.charCodeAt(i)) | 0;
      h2 = ((h2 << 5) + h2 ^ canonical.charCodeAt(i)) | 0;
    }
    return 'i' + (h1 >>> 0).toString(36) + (h2 >>> 0).toString(36);
  }

  function proposalIdForIkey(ikey: string): string {
    return 'p' + ikey.slice(1);
  }

  /* Create an immutable proposal locally. Durable offline; rides the normal
     push when the device next syncs. Idempotent on the contract's identity —
     same rule, target, input vector and candidate returns the existing
     proposal — and a genuine rerun links its predecessor with `pred`. */
  function mdOrganizerPropose(input: unknown): { ok: true; proposal: MdOrganizerProposal; created: boolean }
    | MdOrganizerPlanRefusal {
    if (!input || typeof input !== 'object' || Array.isArray(input)) {
      return refusal('INVALID_PROPOSAL', 'input');
    }
    const raw = input as Record<string, unknown>;
    const rule = text(raw.rule, 60);
    if (!rule) return refusal('INVALID_PROPOSAL', 'rule');
    const rawTarget = raw.target as { edge?: unknown; property?: unknown } | undefined;
    if (!rawTarget || typeof rawTarget !== 'object'
      || !validId(rawTarget.edge) || !validPropertyId(rawTarget.property)) {
      return refusal('INVALID_PROPOSAL', 'target');
    }
    const target = { edge: rawTarget.edge as string, property: rawTarget.property as string };
    const prov = sanitiseProvenance(raw.prov, true);
    if (!prov) return refusal('INVALID_PROPOSAL', 'provenance');

    if (!g.mdMembershipEdgeGet(target.edge)) return refusal('TARGET_MISSING');
    const schema = mdOrganizerSchemaGet(raw.schemaId);
    if (!schema || !schema.properties.some((p) => p.id === target.property)) {
      return refusal('SCHEMA_MISMATCH');
    }
    const validated = mdOrganizerCandidateValidate(raw.schemaId, target.property, raw.candidate);
    if (!validated.ok) return refusal('INVALID_PROPOSAL', 'candidate');
    const candidate = validated.value;
    const policy = mdOrganizerPolicyGet(raw.policyId);
    if (!policy) return refusal('POLICY_MISMATCH');

    const sourceKey = raw.sourceKey == null ? null
      : validSourceKey(raw.sourceKey) ? raw.sourceKey : null;
    if (raw.sourceKey != null && sourceKey == null) {
      return refusal('INVALID_PROPOSAL', 'sourceKey');
    }
    const hasBodyRev = raw.bodyRev != null;
    const hasBodyHash = raw.bodyHash != null;
    if (hasBodyRev !== hasBodyHash) {
      return refusal('INVALID_PROPOSAL', 'source-fingerprint');
    }
    if ((hasBodyRev || hasBodyHash) && sourceKey == null) {
      return refusal('INVALID_PROPOSAL', 'sourceKey');
    }
    if (sourceKey != null && !hasBodyHash) return refusal('INVALID_PROPOSAL', 'source-fingerprint');

    const inputVector: MdOrganizerInputVector = {
      bodyRev: raw.bodyRev == null ? null : intOrNull(raw.bodyRev),
      bodyHash: raw.bodyHash == null ? null : text(raw.bodyHash, 128),
      sourceKey,
      sourceRev: sourceKey
        ? intOrNull(typeof g.mdSyncDocCurrentRev === 'function'
          ? g.mdSyncDocCurrentRev(sourceKey) : g.mdSyncDocRev?.(sourceKey)) : null,
      schema: schema.v,
      schemaId: schema.id,
      targetRev: typeof g.mdSyncDocRev === 'function'
        ? g.mdSyncDocRev('edge:' + target.edge) : null,
      policy: policy.v,
      policyId: policy.id,
    };
    if (raw.bodyRev != null && inputVector.bodyRev == null) return refusal('INVALID_PROPOSAL', 'bodyRev');
    if (raw.bodyHash != null && inputVector.bodyHash == null) return refusal('INVALID_PROPOSAL', 'bodyHash');

    const ikey = idempotencyKey(rule, target, inputVector, candidate);
    const existing = allProposals();
    const duplicate = existing.find((p) => p.ikey === ikey);
    if (duplicate) return { ok: true, proposal: duplicate, created: false };
    const proposalId = proposalIdForIkey(ikey);
    if (g.mdRead(proposalKey(proposalId), null) != null) {
      return refusal('INVALID_PROPOSAL', 'id-collision');
    }

    /* A genuine rerun: newest prior proposal for the same rule and target
       becomes this one's predecessor. Ties break on id so the link is
       deterministic on every device. */
    let pred: string | null = null;
    let predAt = -1;
    for (const p of existing) {
      if (p.rule !== rule || p.target.edge !== target.edge
        || p.target.property !== target.property) continue;
      if (p.at > predAt || (p.at === predAt && pred !== null && p.id > pred)) {
        pred = p.id;
        predAt = p.at;
      }
    }

    const proposal: MdOrganizerProposal = {
      id: proposalId,
      runId: text(raw.runId, 120) || newId('run'),
      ikey,
      rule,
      target,
      candidate,
      origin: 'ai',
      input: inputVector,
      prov,
      pred,
      at: Number.isFinite(raw.at) ? raw.at as number : Date.now(),
    };
    if (!g.mdWrite(proposalKey(proposal.id), proposal)) {
      return refusal('OFFLINE_UNCOMMITTED', 'persist-failed');
    }
    return { ok: true, proposal, created: true };
  }

  /* ── decisions and the state fold ──────────────────────────────────────── */

  const validAct = (value: unknown): value is MdOrganizerAct =>
    value === 'materialize' || value === 'accept' || value === 'reject' || value === 'unlock';
  const validActor = (value: unknown): value is string =>
    value === 'user' || value === 'organizer'
    || (typeof value === 'string' && /^policy:[a-z0-9]{6,40}@\d{1,10}$/.test(value));

  function sanitiseDecision(value: unknown, expectedId?: string): MdOrganizerDecision | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as Partial<MdOrganizerDecision>;
    const id = expectedId || raw.id;
    if (!validId(id) || (raw.id != null && raw.id !== id)) return null;
    if (!validId(raw.pid) || !validAct(raw.act) || !validActor(raw.actor)
      || !Number.isFinite(raw.at)) return null;
    const out: MdOrganizerDecision = {
      id, pid: raw.pid, act: raw.act, actor: raw.actor, at: raw.at as number,
    };
    const reason = text(raw.reason, 80);
    if (reason) out.reason = reason;
    return out;
  }

  function decisionsFor(pid: string): MdOrganizerDecision[] {
    const out: MdOrganizerDecision[] = [];
    for (const key of g.mdStorageKeys('md:decision:')) {
      const id = key.slice('md:decision:'.length);
      const decision = sanitiseDecision(g.mdRead(key, null), id);
      if (decision && decision.pid === pid) out.push(decision);
    }
    return out;
  }

  /* Effective state from append-only events — never from rewriting anything.
     Sorted by (at, id) so every device folds the same way; the first terminal
     decision wins review, and later conflicting ones stay on the record but
     change nothing. */
  function foldDecisions(decisions: MdOrganizerDecision[]): MdOrganizerState {
    const sorted = [...decisions].sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
    const state: MdOrganizerState = {
      review: 'proposed', protection: null, materialized: false, decisions: sorted,
    };
    for (const d of sorted) {
      if (d.act === 'materialize') {
        if (state.review === 'proposed') state.materialized = true;
      } else if (d.act === 'accept') {
        if (state.review === 'proposed') {
          state.review = 'accepted';
          state.protection = 'user-locked';
        }
      } else if (d.act === 'reject') {
        if (state.review === 'proposed') {
          state.review = 'rejected';
          state.protection = null;
        }
      } else if (d.act === 'unlock') {
        if (state.review === 'accepted' && state.protection === 'user-locked') {
          state.protection = 'mutable';
        }
      }
    }
    return state;
  }

  function mdOrganizerState(id: unknown): MdOrganizerState | null {
    const proposal = mdOrganizerProposal(id);
    return proposal ? foldDecisions(decisionsFor(proposal.id)) : null;
  }

  /* ── schema and policy authorities ─────────────────────────────────────── */

  function sanitiseSchema(value: unknown, expectedId?: string): MdOrganizerSchema | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as Partial<MdOrganizerSchema>;
    const id = expectedId || raw.id;
    if (!validId(id) || (raw.id != null && raw.id !== id)) return null;
    if (!text(raw.tag, 60) || !versionOrNull(raw.v) || !Number.isFinite(raw.at)) return null;
    if (!Array.isArray(raw.properties) || raw.properties.length > MAX_SCHEMA_PROPERTIES) return null;
    const properties: MdOrganizerSchema['properties'] = [];
    const seen = new Set<string>();
    for (const item of raw.properties) {
      if (!item || typeof item !== 'object') return null;
      const p = item as { id?: unknown; type?: unknown; label?: unknown };
      if (!validPropertyId(p.id) || !text(p.type, 40) || seen.has(p.id as string)) return null;
      seen.add(p.id as string);
      const entry: { id: string; type: string; label?: string } = {
        id: p.id as string, type: p.type as string,
      };
      const label = text(p.label, 120);
      if (label) entry.label = label;
      properties.push(entry);
    }
    return { id, tag: raw.tag as string, v: raw.v as number, properties, at: raw.at as number };
  }

  function sanitisePolicy(value: unknown, expectedId?: string): MdOrganizerPolicy | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as Partial<MdOrganizerPolicy>;
    const id = expectedId || raw.id;
    if (!validId(id) || (raw.id != null && raw.id !== id)) return null;
    if (!text(raw.scope, 60) || !versionOrNull(raw.v) || !Number.isFinite(raw.at)) return null;
    if (typeof raw.autofill !== 'number' || !Number.isFinite(raw.autofill)
      || raw.autofill < 0 || raw.autofill > 1) return null;
    return {
      id, scope: raw.scope as string, v: raw.v as number,
      autofill: raw.autofill, at: raw.at as number,
    };
  }

  function mdOrganizerSchemaSave(input: unknown): MdOrganizerSchema | null {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
    const raw = input as Record<string, unknown>;
    const schema = sanitiseSchema({
      id: validId(raw.id) ? raw.id : newId('s'),
      tag: raw.tag,
      v: raw.v,
      properties: Array.isArray(raw.properties) ? raw.properties : [],
      at: Number.isFinite(raw.at) ? raw.at : Date.now(),
    });
    if (!schema || !g.mdWrite('md:orgschema:' + schema.id, schema)) return null;
    return schema;
  }

  function mdOrganizerSchemaGet(id: unknown): MdOrganizerSchema | null {
    if (!validId(id)) return null;
    return sanitiseSchema(g.mdRead('md:orgschema:' + id, null), id);
  }

  function mdOrganizerPolicySave(input: unknown): MdOrganizerPolicy | null {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
    const raw = input as Record<string, unknown>;
    const policy = sanitisePolicy({
      id: validId(raw.id) ? raw.id : newId('y'),
      scope: raw.scope,
      v: raw.v,
      autofill: raw.autofill,
      at: Number.isFinite(raw.at) ? raw.at : Date.now(),
    });
    if (!policy || !g.mdWrite('md:orgpolicy:' + policy.id, policy)) return null;
    return policy;
  }

  function mdOrganizerPolicyGet(id: unknown): MdOrganizerPolicy | null {
    if (!validId(id)) return null;
    return sanitisePolicy(g.mdRead('md:orgpolicy:' + id, null), id);
  }

  /* ── the pure planner ──────────────────────────────────────────────────── */

  function retainedRev(key: string): number | null {
    return typeof g.mdSyncDocRev === 'function' ? g.mdSyncDocRev(key) : null;
  }

  /* One intent in, one answer out: either the complete guarded mutation —
     read-set and write-set, nothing else — or a named semantic refusal.
     Pure: reads the world, changes nothing, so a retry is simply calling it
     again after a pull. */
  function mdOrganizerPlan(proposalId: unknown, intent: unknown): MdOrganizerPlan {
    const proposal = mdOrganizerProposal(proposalId);
    if (!proposal) return refusal('INVALID_PROPOSAL', 'unknown-proposal');
    const act = intent && typeof intent === 'object'
      ? (intent as { act?: unknown }).act : undefined;
    if (!validAct(act)) return refusal('INVALID_PROPOSAL', 'unknown-intent');

    const state = foldDecisions(decisionsFor(proposal.id));
    if (state.review === 'rejected') return refusal('ALREADY_DECIDED', 'rejected');
    if (act === 'materialize' && state.materialized) return refusal('ALREADY_DECIDED', 'materialized');
    if (act === 'accept' && state.review === 'accepted') return refusal('ALREADY_DECIDED', 'accepted');
    if (act === 'reject' && state.review === 'accepted') return refusal('ALREADY_DECIDED', 'accepted');
    if (act === 'unlock' && state.review !== 'accepted') return refusal('INVALID_PROPOSAL', 'not-accepted');
    if (act === 'unlock' && state.protection !== 'user-locked') return refusal('ALREADY_DECIDED', 'unlocked');

    const cellIntent = act !== 'reject' || state.materialized;
    const edgeIntent = cellIntent || act === 'reject';
    const sourceIntent = act === 'materialize' || act === 'accept';
    const observedBodyHash = intent && typeof intent === 'object'
      ? (intent as { currentBodyHash?: unknown }).currentBodyHash : undefined;
    if (sourceIntent && proposal.input.bodyHash != null) {
      if (!text(observedBodyHash, 128)) {
        return refusal('STALE_INPUT', 'source-fingerprint-missing');
      }
      if (observedBodyHash !== proposal.input.bodyHash) {
        return refusal('STALE_INPUT', 'source-body');
      }
    }
    const prop = proposal.target.property;
    let edge: MdOrganizerEdge | null = null;
    let meta: MdOrganizerCellMeta | undefined;
    let schema: MdOrganizerSchema | null = null;
    let policy: MdOrganizerPolicy | null = null;

    if (edgeIntent) {
      edge = g.mdMembershipEdgeGet(proposal.target.edge);
      if (!edge) return refusal('TARGET_MISSING');
    }
    if (cellIntent && edge) {
      schema = mdOrganizerSchemaGet(proposal.input.schemaId);
      if (!schema || schema.v !== proposal.input.schema
        || !schema.properties.some((p) => p.id === prop)) {
        return refusal('SCHEMA_MISMATCH');
      }
      const candidate = mdOrganizerCandidateValidate(
        proposal.input.schemaId, prop, proposal.candidate);
      if (!candidate.ok
        || planSignatureStable(candidate.value) !== planSignatureStable(proposal.candidate)) {
        return refusal('INVALID_PROPOSAL', 'candidate');
      }
      policy = mdOrganizerPolicyGet(proposal.input.policyId);
      if (!policy || policy.v !== proposal.input.policy) return refusal('POLICY_MISMATCH');
      if (act === 'materialize' && proposal.prov.conf < policy.autofill) {
        return refusal('BELOW_THRESHOLD');
      }

      meta = edge.m ? edge.m[prop] : undefined;
      const hasValue = Object.prototype.hasOwnProperty.call(edge.v, prop);

      /* A user-locked value always wins — even when every revision matches.
         The one exception is the lock's own accepted proposal asking to be
         unlocked. A value carrying no provenance at all is the user's own
         writing from before this map existed; it is treated as locked too,
         because the organizer's first rule is that it never overwrites. */
      if (meta && meta.p === 'user-locked') {
        const ownLock = act === 'unlock' && meta.r === 'accepted'
          && meta.prov && meta.prov.pid === proposal.id;
        if (!ownLock) return refusal('USER_LOCKED');
      }
      if ((act === 'materialize' || (act === 'accept' && !state.materialized))
        && hasValue && !meta) {
        return refusal('USER_LOCKED', 'unowned-value');
      }
      if (act === 'materialize' && meta && meta.r === 'proposed'
        && meta.prov && meta.prov.pid === proposal.id) {
        return refusal('ALREADY_DECIDED', 'materialized');
      }
      /* Accepting locks the exact cell the user is looking at: it must still
         be this proposal's proposed materialization. If the cell moved on,
         the honest answer is staleness, not a clobber. */
      if (act === 'accept' && state.materialized) {
        if (!meta || meta.r !== 'proposed' || !meta.prov || meta.prov.pid !== proposal.id) {
          return refusal('STALE_INPUT', 'cell-moved');
        }
      }
      if (act === 'unlock'
        && (!meta || meta.r !== 'accepted' || !meta.prov || meta.prov.pid !== proposal.id)) {
        return refusal('STALE_INPUT', 'cell-moved');
      }
      if (act === 'reject' && state.materialized
        && (!meta || meta.r !== 'proposed' || !meta.prov || meta.prov.pid !== proposal.id)) {
        return refusal('STALE_INPUT', 'cell-moved');
      }
    }

    /* Revision pins. Every guarded document must have a retained server
       revision; absent one, the mutation cannot be defended and is named
       rather than attempted. */
    const pKey = 'proposal:' + proposal.id;
    const revProposal = retainedRev(pKey);
    if (revProposal == null) return refusal('OFFLINE_UNCOMMITTED', pKey);
    const readSet: MdOrganizerPlanOk['readSet'] = [{ key: pKey, expectedRev: revProposal }];

    if (sourceIntent && proposal.input.bodyHash != null) {
      if (!proposal.input.sourceKey || proposal.input.sourceRev == null) {
        return refusal('OFFLINE_UNCOMMITTED', proposal.input.sourceKey || 'source-document');
      }
      const currentSourceRev = retainedRev(proposal.input.sourceKey);
      if (currentSourceRev == null) {
        return refusal('OFFLINE_UNCOMMITTED', proposal.input.sourceKey);
      }
      if (currentSourceRev !== proposal.input.sourceRev) {
        return refusal('STALE_INPUT', 'source-revision');
      }
      readSet.push({ key: proposal.input.sourceKey, expectedRev: currentSourceRev });
    }

    let revEdge: number | null = null;
    if (edgeIntent && edge) {
      const eKey = 'edge:' + edge.id;
      revEdge = retainedRev(eKey);
      if (revEdge == null) return refusal('OFFLINE_UNCOMMITTED', eKey);
      let sKey: string | null = null;
      let yKey: string | null = null;
      let revSchema: number | null = null;
      let revPolicy: number | null = null;
      if (cellIntent) {
        sKey = 'orgschema:' + proposal.input.schemaId;
        yKey = 'orgpolicy:' + proposal.input.policyId;
        revSchema = retainedRev(sKey);
        revPolicy = retainedRev(yKey);
        if (revSchema == null) return refusal('OFFLINE_UNCOMMITTED', sKey);
        if (revPolicy == null) return refusal('OFFLINE_UNCOMMITTED', yKey);
      }
      /* The candidate was derived from the edge as it was; if the edge has
         moved since, the candidate is stale even though the commit would be
         CAS-clean. Accept/unlock guard the CURRENT revision instead — their
         freshness check is the cell-metadata check above. */
      if (act === 'materialize' && proposal.input.targetRev !== revEdge) {
        return refusal('STALE_INPUT', 'target-revision');
      }
      readSet.push({ key: eKey, expectedRev: revEdge });
      if (sKey && yKey && revSchema != null && revPolicy != null) {
        readSet.push({ key: sKey, expectedRev: revSchema },
          { key: yKey, expectedRev: revPolicy });
      }
    }

    const now = Date.now();
    const decision: MdOrganizerDecision = {
      id: newId('d'),
      pid: proposal.id,
      act,
      actor: act === 'materialize'
        ? `policy:${proposal.input.policyId}@${proposal.input.policy}` : 'user',
      at: now,
    };
    readSet.push({ key: 'decision:' + decision.id, expectedRev: 'ABSENT' });

    const docs: MdOrganizerPlanOk['docs'] = [];
    if (edgeIntent && edge) {
      const nextEdge: MdOrganizerEdge = {
        ...edge, v: { ...edge.v }, ...(edge.m ? { m: { ...edge.m } } : {}), at: now,
      };
      let nextM = nextEdge.m;
      if (cellIntent && !nextM) {
        nextM = {};
        nextEdge.m = nextM;
      }
      if (act === 'materialize') {
        nextEdge.v[prop] = proposal.candidate;
        (nextM as Record<string, MdOrganizerCellMeta>)[prop] = {
          o: 'ai', r: 'proposed', p: 'mutable', prov: compactProvenance(proposal, now),
        };
      } else if (act === 'accept' && !state.materialized) {
        nextEdge.v[prop] = proposal.candidate;
        (nextM as Record<string, MdOrganizerCellMeta>)[prop] = {
          o: 'ai', r: 'accepted', p: 'user-locked', prov: compactProvenance(proposal, now),
        };
      } else if (act === 'accept') {
        /* Locks the exact accepted cell: value, origin and provenance stay
           byte-for-byte; only review and protection move. */
        (nextM as Record<string, MdOrganizerCellMeta>)[prop] = {
          ...(meta as MdOrganizerCellMeta), r: 'accepted', p: 'user-locked',
        };
      } else if (act === 'unlock') {
        /* Protection only. Origin, review history, candidate and provenance
           all remain exactly as they were. */
        (nextM as Record<string, MdOrganizerCellMeta>)[prop] = {
          ...(meta as MdOrganizerCellMeta), p: 'mutable',
        };
      } else if (act === 'reject' && state.materialized) {
        /* Undo of a still-proposed auto-materialization. The immutable proposal
           and decision history remain; only its active overlay is removed. */
        delete nextEdge.v[prop];
        delete (nextM as Record<string, MdOrganizerCellMeta>)[prop];
      }
      docs.push({ key: 'edge:' + edge.id, value: nextEdge });
    }
    docs.push({ key: 'decision:' + decision.id, value: decision });

    /* The snapshot binds the plan to the exact semantic world it was derived
       from: the full sanitised proposal, plus the guarded edge and authority
       documents when the act reads them. The executor's signature covers it
       whole, so a local rewrite of the proposal's candidate or provenance
       after issue — invisible in a reject plan's own output — still kills
       the plan before anything is transported. */
    const snapshot: MdOrganizerPlanSnapshot = { proposal };
    if (edgeIntent && edge) {
      snapshot.edge = edge;
      if (schema) snapshot.schema = schema;
      if (policy) snapshot.policy = policy;
    }

    return {
      ok: true, pid: proposal.id, act, readSet, docs, snapshot,
      observedBodyHash: typeof observedBodyHash === 'string' ? observedBodyHash : null,
    };
  }

  /* ── the executor ──────────────────────────────────────────────────────── */

  /* ── plan signatures — what "the same plan" means ──────────────────────── */

  /* The planner is deterministic in everything that carries semantics; only
     the decision's identity and the wall-clock stamps vary between two runs
     over the same world. The signature canonicalises exactly those volatile
     slots — decision id, decision.at, edge.at, cell provenance at — so a
     plan can be compared against a fresh planner run field-for-field. */
  function planSignatureStable(value: unknown): string {
    if (Array.isArray(value)) {
      return '[' + value.map(planSignatureStable).join(',') + ']';
    }
    if (value && typeof value === 'object') {
      return '{' + Object.keys(value as Record<string, unknown>).sort()
        .map((k) => JSON.stringify(k) + ':' + planSignatureStable((value as Record<string, unknown>)[k]))
        .join(',') + '}';
    }
    return JSON.stringify(value) as string;
  }

  /* The edge canonicalisation, shared by the write-set and the snapshot:
     wall-clock stamps (edge.at, cell provenance at) are volatile between two
     planner runs over the same world; everything else is semantics. */
  function planSignatureEdge(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const edge = { ...(value as Record<string, unknown>) };
    delete edge.at;
    if (edge.m && typeof edge.m === 'object') {
      const meta: Record<string, unknown> = {};
      for (const [prop, cell] of Object.entries(edge.m as Record<string, unknown>)) {
        if (cell && typeof cell === 'object') {
          const c = { ...(cell as Record<string, unknown>) };
          if (c.prov && typeof c.prov === 'object') {
            const prov = { ...(c.prov as Record<string, unknown>) };
            delete prov.at;
            c.prov = prov;
          }
          meta[prop] = c;
        } else {
          meta[prop] = cell;
        }
      }
      edge.m = meta;
    }
    return edge;
  }

  /* The snapshot canonicalised the same way: the proposal in full (it is
     immutable — every field is semantics), the edge minus its volatile
     stamps, the authorities in full. Anything missing or misshapen is not a
     plan the planner could have issued. */
  function planSignatureSnapshot(snapshot: unknown): Record<string, unknown> | null {
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return null;
    const s = snapshot as Record<string, unknown>;
    if (!s.proposal || typeof s.proposal !== 'object' || Array.isArray(s.proposal)) return null;
    const out: Record<string, unknown> = { proposal: s.proposal };
    for (const slot of ['edge', 'schema', 'policy'] as const) {
      const v = s[slot];
      if (v == null) continue;
      if (typeof v !== 'object' || Array.isArray(v)) return null;
      if (slot === 'edge') {
        const edge = planSignatureEdge(v);
        if (!edge) return null;
        out[slot] = edge;
      } else {
        out[slot] = v;
      }
    }
    return out;
  }

  function planSignature(plan: MdOrganizerPlanOk): string | null {
    if (!validId(plan.pid) || !validAct(plan.act)) return null;
    if (!Array.isArray(plan.readSet) || !plan.readSet.length) return null;
    if (!Array.isArray(plan.docs) || !plan.docs.length) return null;
    const observedBodyHash = plan.observedBodyHash == null
      ? null : text(plan.observedBodyHash, 128);
    if (plan.observedBodyHash != null && !observedBodyHash) return null;

    let decisionKey: string | null = null;
    const readSet = plan.readSet.map((entry) => {
      if (!entry || typeof entry !== 'object' || typeof entry.key !== 'string') {
        return '<invalid>';
      }
      if (entry.expectedRev === 'ABSENT') {
        if (decisionKey !== null || !entry.key.startsWith('decision:')) return '<invalid>';
        decisionKey = entry.key;
        return 'decision:*=ABSENT';
      }
      if (!Number.isInteger(entry.expectedRev) || (entry.expectedRev as number) < 0) {
        return '<invalid>';
      }
      return entry.key + '=' + String(entry.expectedRev);
    });
    if (decisionKey === null || readSet.some((s) => s === '<invalid>')) return null;

    const docs = plan.docs.map((doc) => {
      if (!doc || typeof doc !== 'object' || typeof doc.key !== 'string') return '<invalid>';
      let key = doc.key;
      let value = doc.value;
      if (key.startsWith('decision:')) {
        if (key !== decisionKey || !value || typeof value !== 'object') return '<invalid>';
        const { id: _id, at: _at, ...rest } = value as Record<string, unknown>;
        key = 'decision:*';
        value = rest;
      } else if (key.startsWith('edge:')) {
        const edge = planSignatureEdge(value);
        if (!edge) return '<invalid>';
        value = edge;
      } else {
        return '<invalid>';
      }
      return key + '=' + planSignatureStable(value);
    });
    if (docs.some((s) => s === '<invalid>')) return null;

    const snapshot = planSignatureSnapshot((plan as { snapshot?: unknown }).snapshot);
    if (!snapshot) return null;

    return planSignatureStable({
      pid: plan.pid, act: plan.act, readSet, docs, snapshot,
      observedBodyHash,
    });
  }

  /* The only way a cell value changes hands — and the only way a decision is
     ever written. The executor accepts nothing on the caller's word: it
     re-derives the plan from the planner against the world as it is RIGHT
     NOW and transports only what the planner itself just issued.

       - FORGED PLANS DIE HERE. An object that merely looks like a plan —
         invented, truncated, reordered, or mutated after issue — cannot
         match the planner's own fresh output field-for-field, and is named
         INVALID_PROPOSAL before anything touches the transport.
       - STALE PLANS DIE HERE. The fresh run re-checks every semantic guard
         against current local state: a user edit that landed after the plan
         was issued (a value the server revisions cannot see, because it has
         not synced yet) turns the fresh plan into the named refusal —
         USER_LOCKED, STALE_INPUT, ALREADY_DECIDED — and nothing is
         transported. The plan is bound to the local semantic snapshot it was
         derived from, not just to server revisions.

     Applies through the A9 chokepoint and touches local storage only after
     the Worker has accepted the whole group — so STALE_READ_SET means
     "nothing changed, anywhere", and a network failure is named
     OFFLINE_UNCOMMITTED rather than mistaken for a decision that
     happened. */
  async function mdOrganizerApply(plan: unknown, context?: unknown): Promise<MdOrganizerPlan
    | { ok: true; pid: string; act: MdOrganizerAct; rev: number }> {
    if (!plan || typeof plan !== 'object') return refusal('INVALID_PROPOSAL', 'no-plan');
    if ((plan as { ok?: unknown }).ok !== true) return plan as MdOrganizerPlanRefusal;
    const p = plan as MdOrganizerPlanOk;
    const submittedSignature = planSignature(p);
    if (!submittedSignature) return refusal('INVALID_PROPOSAL', 'bad-plan-shape');
    if (typeof g.mdSyncCommit !== 'function') return refusal('OFFLINE_UNCOMMITTED', 'no-transport');

    /* Revalidate against the world as it is now, immediately before submit.
       A refusal here is the semantic answer — pass it through untouched. */
    const currentBodyHash = context && typeof context === 'object'
      ? (context as { currentBodyHash?: unknown }).currentBodyHash : undefined;
    const fresh = mdOrganizerPlan(p.pid, { act: p.act, currentBodyHash });
    if (!fresh.ok) return fresh;
    if (planSignature(fresh) !== submittedSignature) {
      return refusal('INVALID_PROPOSAL', 'plan-not-planner-issued');
    }

    const res = await g.mdSyncCommit({ readSet: fresh.readSet, docs: fresh.docs });
    if (!res || res.ok !== true) {
      if (res && res.error === 'STALE_READ_SET') {
        return { ok: false, refusal: 'STALE_READ_SET', conflicts: res.conflicts };
      }
      return refusal('OFFLINE_UNCOMMITTED', (res && res.error) || 'network');
    }

    for (const doc of fresh.docs) {
      if (doc.key.startsWith('edge:')) {
        g.mdMembershipApplyDoc(doc.key, doc.value);
      } else if (doc.key.startsWith('decision:')) {
        g.mdWrite('md:' + doc.key, doc.value);
      }
    }
    return { ok: true, pid: fresh.pid, act: fresh.act, rev: res.rev };
  }

  /* ── sync documents ────────────────────────────────────────────────────── */

  const SERVER_KEY_RE = /^(proposal|decision|orgschema|orgpolicy):([a-z0-9]{6,40})$/;

  function mdOrganizerSyncDocs(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const key of g.mdStorageKeys()) {
      if (!key.startsWith('md:')) continue;
      const serverKey = key.slice(3);
      if (!SERVER_KEY_RE.test(serverKey)) continue;
      const value = g.mdRead(key, null);
      /* Audit stubs are retained authority, not live proposals. They ride sync
         with an explicit transport tombstone flag added by md-sync.js; the
         organizer sanitiser keeps them distinct from planner-readable input. */
      if (value != null) out[serverKey] = value;
    }
    return out;
  }

  /* Pulled documents are untrusted input written by another device, possibly
     mid-write. A malformed one is rejected whole — dropped, not repaired —
     because half a proposal is a decision waiting to be misread. */
  function mdOrganizerSanitiseSyncDoc(key: unknown, value: unknown): unknown {
    if (typeof key !== 'string') return undefined;
    const m = SERVER_KEY_RE.exec(key);
    if (!m) return undefined;
    if (value == null) return null;
    const id = m[2] as string;
    if (m[1] === 'proposal') {
      if (isProposalStub(value)) {
        const stub = value as unknown as Record<string, unknown>;
        const review = stub.review;
        if (!(stub.at === null || Number.isFinite(stub.at))
          || !Number.isFinite(stub.tombstonedAt)
          || !(stub.hash === null || typeof stub.hash === 'string')
          || !(review === null || review === 'proposed' || review === 'accepted' || review === 'rejected')) {
          return undefined;
        }
        return {
          id, tombstone: true, at: stub.at, tombstonedAt: stub.tombstonedAt,
          hash: stub.hash, review,
        };
      }
      return sanitiseProposal(value, id) || undefined;
    }
    if (m[1] === 'decision') return sanitiseDecision(value, id) || undefined;
    if (m[1] === 'orgschema') return sanitiseSchema(value, id) || undefined;
    return sanitisePolicy(value, id) || undefined;
  }

  /* PROPOSALS AND DECISIONS ARE CREATION-ONLY ACROSS SYNC, AND THE SERVER'S
     FIRST COPY IS CANONICAL. Either family is born once, complete and final,
     and the server enforces that in both write endpoints — so the live copy
     it holds under an id IS the first write that reached it. When a pull
     carries a genuinely different document under an id this device already
     holds (a same-id race, a rewrite attempt, an offline draft that lost),
     the local copy yields: keeping it while recording the incoming copy's
     hash and revision would leave local data, sync bookkeeping and server
     authority permanently disagreeing. An identical echo is a no-op; a
     different one is adopted, and every device converges on the server's
     copy. Schemas and policies are versioned authorities and keep ordinary
     last-writer sync — a new version of the same authority id is exactly how
     they move. */
  function isProposalStub(value: unknown): value is MdOrganizerProposalStub {
    return !!value && typeof value === 'object' && !Array.isArray(value)
      && (value as { tombstone?: unknown }).tombstone === true
      && validId((value as { id?: unknown }).id);
  }

  /* The stub's content hash: double-djb2 over the canonical serialisation,
     the same construction as the idempotency key — only ever compared to
     itself, so 64 bits of it makes a collision a non-event. */
  function proposalContentHash(proposal: MdOrganizerProposal): string {
    const canonical = planSignatureStable(proposal);
    let h1 = 5381; let h2 = 52711;
    for (let i = 0; i < canonical.length; i++) {
      h1 = ((h1 << 5) + h1 + canonical.charCodeAt(i)) | 0;
      h2 = ((h2 << 5) + h2 ^ canonical.charCodeAt(i)) | 0;
    }
    return 'h' + (h1 >>> 0).toString(36) + (h2 >>> 0).toString(36);
  }

  /* A proposal tombstone folds into the audit stub Scenario 4 specifies:
     IDs/hashes/status/timestamps stay; the proposed content and its evidence
     are removed. */
  function tombstoneProposal(localKey: string, id: string): boolean {
    const raw = g.mdRead(localKey, null);
    /* A tombstone is terminal: the first stub for an id stands, unchanged.
       Without this, every repeated cleanup of the same target rewrites the
       stub with a fresh stamp, and each rewrite is a new document revision
       that has to ride the next push — pure churn for a fact that already
       converged. */
    if (isProposalStub(raw)) return true;
    const existing = sanitiseProposal(raw, id);
    const stub: MdOrganizerProposalStub = {
      id,
      tombstone: true,
      at: existing ? existing.at : null,
      tombstonedAt: Date.now(),
      hash: existing ? proposalContentHash(existing) : null,
      review: existing ? foldDecisions(decisionsFor(id)).review : null,
    };
    return g.mdWrite(localKey, stub);
  }

  /* Newest first, ties broken on id — the order the set was indexed in. The
     copy is deep because the caller used to get freshly parsed documents every
     time and could do as it liked with them; now it is holding the cache's own
     proposals, and a caller that edited one in place would be editing what
     every later reader sees. */
  function mdOrganizerProposalsForTarget(edgeId: unknown): MdOrganizerProposal[] {
    if (!validId(edgeId)) return [];
    const held = proposalSet().byTarget.get(edgeId);
    return held ? held.map((proposal) => JSON.parse(JSON.stringify(proposal))) : [];
  }

  function mdOrganizerTombstoneTarget(edgeId: unknown): boolean {
    const proposals = mdOrganizerProposalsForTarget(edgeId);
    let ok = true;
    for (const proposal of proposals) {
      if (!tombstoneProposal(proposalKey(proposal.id), proposal.id)) ok = false;
    }
    return ok;
  }

  function mdOrganizerApplyDoc(key: unknown, value: unknown): boolean {
    if (typeof key !== 'string') return false;
    const m = SERVER_KEY_RE.exec(key);
    if (!m) return false;
    const localKey = 'md:' + key;
    if (value == null) {
      /* Decision history is NEVER tombstoned: the pull is declined, not
         applied — and the false return keeps the sync bookkeeping from
         recording a document this device deliberately kept. */
      if (m[1] === 'decision') return false;
      if (m[1] === 'proposal') return tombstoneProposal(localKey, m[2] as string);
      return g.mdRemove(localKey);
    }
    const clean = mdOrganizerSanitiseSyncDoc(key, value);
    if (clean === undefined || clean === null) return false;
    if (m[1] === 'proposal' || m[1] === 'decision') {
      const id = m[2] as string;
      const raw = g.mdRead(localKey, null);
      if (m[1] === 'proposal' && isProposalStub(clean)) {
        if (isProposalStub(raw)
          && planSignatureStable(raw) === planSignatureStable(clean)) return true;
        return g.mdWrite(localKey, clean);
      }
      /* A tombstoned id never reactivates — declined, not applied. */
      if (isProposalStub(raw)) return false;
      const existing = m[1] === 'proposal'
        ? sanitiseProposal(raw, id)
        : sanitiseDecision(raw, id);
      if (existing) {
        /* The echo of what this device already holds is a no-op. A genuinely
           different document under the same id is the server's canonical
           first copy, and the local copy yields to it. */
        if (planSignatureStable(existing) === planSignatureStable(clean)) return true;
        return g.mdWrite(localKey, clean);
      }
    }
    return g.mdWrite(localKey, clean);
  }

  Object.assign(g, {
    mdOrganizerPropose, mdOrganizerProposal, mdOrganizerState,
    mdOrganizerPlan, mdOrganizerApply,
    mdOrganizerSchemaSave, mdOrganizerSchemaGet,
    mdOrganizerPolicySave, mdOrganizerPolicyGet,
    mdOrganizerCandidateValidate, mdOrganizerProposalsForTarget, mdOrganizerTombstoneTarget,
    mdOrganizerSyncDocs, mdOrganizerSanitiseSyncDoc, mdOrganizerApplyDoc,
  });

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = {
      mdOrganizerPropose, mdOrganizerProposal, mdOrganizerState,
      mdOrganizerPlan, mdOrganizerApply,
      mdOrganizerSchemaSave, mdOrganizerSchemaGet,
      mdOrganizerPolicySave, mdOrganizerPolicyGet,
      mdOrganizerCandidateValidate, mdOrganizerProposalsForTarget, mdOrganizerTombstoneTarget,
      mdOrganizerSyncDocs, mdOrganizerSanitiseSyncDoc, mdOrganizerApplyDoc,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
